import React, {Suspense, useCallback, useState} from 'react';
import ReactDatePicker from 'react-datepicker';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import clsx from 'clsx';
import {getYYYYMMddFormat} from '@di-shared/api/di/utils';
import {ERROR} from '@di-shared/const';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import {getDateFnsLocale, useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {HistoryDateProps} from '@di-shared/features/history/date/HistoryDate/index';
import HistoryDateCalendar from '@di-shared/features/history/date/HistoryDateCalendar';
import HistoryDateContent from '@di-shared/features/history/date/HistoryDateContent';
import {createHistoryFilterSearch, getDefaultHistoryFilterState} from '@di-shared/features/history/filter/historyFilterQuery';
import {ROUTE_PATHS} from '@di-shared/routes';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useDashedDatetimeFormatter from '@di-shared/utils/useDashedDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {ClickAwayListener} from '@mui/base';
import {isSameDate, isSameYearAndMonth, startDateOfMonth} from '@shared/dateUtils';

export default function ({vin, date}: HistoryDateProps) {
  return (
    <ErrorBoundary fallback={<FallbackError selectedDate={date} />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner
          vin={vin}
          date={date}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, date}: HistoryDateProps) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const [innerDate, setInnerDate] = useState(date);
  const [isMonthChange, setIsMonthChange] = useState(false);
  const [isMonthMode, setIsMonthMode] = useState(true);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  /**
   * [2026-03-12:Jeyoung Lee]
   * HistoryDateCalendar의 setSelectedDate props가 Dispatch<SetStateAction<Date>> 타입을 요구하므로
   * 함수형 업데이트 (prevState => newState) 형태도 지원해야 함
   * 날짜 변경 시 로컬 state 업데이트와 함께 URL도 업데이트하여 브라우저 뒤로가기 시 일관성 유지
   */
  const navigateToDate = useCallback<React.Dispatch<React.SetStateAction<Date>>>(
    (value) => {
      const newDate = value instanceof Function ? value(innerDate) : value;
      setInnerDate(newDate);
      navigate(ROUTE_PATHS.HISTORY_$DATE.getFullPath(vin, getYYYYMMddFormat(newDate)), {replace: true});
    },
    [vin, navigate, innerDate]
  );

  return (
    <div className="wrap has_driving_sheet">
      <Header
        left={<HeaderBackButton />}
        right={
          <div className="nav_box">
            <button
              type="button"
              className="btn btn_ic"
              aria-label=""
              onClick={() => {
                navigate({
                  pathname: ROUTE_PATHS.HISTORY_FILTERED_FILTER.getFullPath(vin),
                  search: createHistoryFilterSearch({
                    ...getDefaultHistoryFilterState(),
                    sourceDate: innerDate,
                  }),
                });
              }}
            >
              <span
                className="ic ic_filter_20px"
                aria-hidden="true"
              ></span>
            </button>
            <ClickAwayListener onClickAway={() => setIsMoreMenuOpen(false)}>
              <div className="btn_more_wrap">
                <button
                  type="button"
                  className="btn btn_ic"
                  aria-label="More"
                  aria-expanded={isMoreMenuOpen}
                  aria-haspopup="menu"
                  aria-controls="history-more-menu"
                  onClick={() => setIsMoreMenuOpen((prev) => !prev)}
                >
                  <span
                    className="ic ic_menu_24px"
                    aria-hidden="true"
                  ></span>
                </button>
                {isMoreMenuOpen && (
                  <ul
                    id="history-more-menu"
                    className="more_menu"
                    role="menu"
                  >
                    <li
                      role="none"
                      className="more_menu_item"
                    >
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setIsMoreMenuOpen(false);
                          navigate(ROUTE_PATHS.HISTORY_DOWNLOAD.getFullPath(vin));
                        }}
                      >
                        {t('ctn_mytrips_dropdown_download_eu')}
                      </button>
                    </li>
                    <li
                      role="none"
                      className="more_menu_item"
                    >
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setIsMoreMenuOpen(false);
                          navigate(ROUTE_PATHS.HISTORY_TAGS.getFullPath(vin));
                        }}
                      >
                        {t('ctn_mytrips_dropdown_manage_tags_eu')}
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            </ClickAwayListener>
          </div>
        }
      >
        <h1 className="headline_xxsmall nav_center_title" />
      </Header>
      <main
        className="content page_my_trips"
        id="content"
      >
        <div className={clsx('driving_sheet_fixed', {'is-collapse': !isMonthMode})}>
          <div className="trip_calendar">
            <HistoryDateCalendar
              vin={vin}
              isMonthMode={isMonthMode}
              selectedDate={innerDate}
              setSelectedDate={navigateToDate}
              isMonthChange={isMonthChange}
              setIsMonthChange={setIsMonthChange}
            />
          </div>
          <div className="driving_sheet_header">
            <button
              className="btn_drawer"
              onClick={() => setIsMonthMode((prev) => !prev)}
            />
          </div>
        </div>
        <div className="driving_sheet_content">
          <HistoryDateContent
            vin={vin}
            selectedDate={innerDate}
            isMonthChange={isMonthChange}
          />
        </div>
      </main>
    </div>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();
  const {defaultDistanceSymbol, defaultEnergyEfficiencySymbol, defaultSpeedSymbol} = useUnitConverter();
  const today = new Date();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const DashedDatetimeFormatter = useDashedDatetimeFormatter();

  return (
    <div
      className="wrap has_driving_sheet"
      aria-hidden="true"
    >
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">
          <span className="blind">{t('ctn_mytrips_all')}</span>
        </h1>
      </Header>
      <main
        className="content page_my_trips"
        id="content"
      >
        <div className="driving_sheet_fixed">
          <div className="trip_calendar">
            <div className="react-datepicker">
              <div className="react-datepicker__month-container skeleton">
                <div className="react-datepicker__header">
                  <div className="react-datepicker__current-month skeleton">
                    <button
                      type="button"
                      className="react-datepicker__navigation react-datepicker__navigation--previous"
                      aria-label="Previous Month"
                    >
                      <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">Previous Month</span>
                    </button>
                    {CldrDatetimeFormatter.format(today, 'yMMM')}
                    <button
                      type="button"
                      className="react-datepicker__navigation react-datepicker__navigation--next navigation--disabled"
                      aria-label="Next Month"
                    >
                      <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">Next Month</span>
                    </button>
                  </div>
                  <div
                    className="react-datepicker__day-names"
                    style={{height: '260px'}}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="driving_sheet_header">
            <button className="btn_drawer">
              <span className="blind">{t('ctn_dss_monthly_chart_before')}</span>
            </button>
          </div>
        </div>
        <div className="driving_sheet_content">
          <div className="trip_summary">
            <div className="trip_date skeleton">
              <span className="trip_date_text">{CldrDatetimeFormatter.format(today, 'MMMMEEEEd')}</span>
              <span className="trip_date_today">{t('ctn_mytrips_details_today')}</span>
            </div>
            <div className="trip_summary_list skeleton">
              <div className="trip_summary_item">
                <span className="trip_summary_title">{t('ctn_mytrips_details_distance')}</span>
                <div className="trip_summary_info">
                  <span className="trip_summary_value">{ERROR.DASH}</span>
                  <span className="trip_summary_unit">{defaultDistanceSymbol}</span>
                </div>
              </div>
              <div className="divider divider_vertical" />
              <div className="trip_summary_item">
                <span className="trip_summary_title">{t('ctn_mytrips_details_time')}</span>
                <div className="trip_summary_info">
                  <span className="trip_summary_value">{ERROR.DASH}</span>
                  <span className="trip_summary_unit">{t('ctn_mytrips_details_time_h_01')}</span>
                  <span className="trip_summary_value">{ERROR.DASH}</span>
                  <span className="trip_summary_unit">{t('ctn_mytrips_details_time_min_01')}</span>
                </div>
              </div>
              <div className="divider divider_vertical" />
              <div className="trip_summary_item">
                <span className="trip_summary_title">{t('ctn_mytrips_details_driving_safety_score')}</span>
                <div className="trip_summary_info">
                  <span className="trip_summary_value">{ERROR.DASH}</span>
                  <span className="trip_summary_unit">{defaultEnergyEfficiencySymbol}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="trip_info skeleton">
            <span className="blind">trip info detail</span>
            <div className="trip_info_head">
              <span className="trip_info_item">{DashedDatetimeFormatter.formatRange(today, today, 'time')}</span>
              <span className="trip_info_item">
                {ERROR.DASH}
                {defaultDistanceSymbol}
              </span>
            </div>
          </div>
          <ul className="statistics_record_list skeleton">
            <li className="statistics_record_item">
              <span className="statistics_record_key">{t('ctn_mytrips_details_idle_time')}</span>
              <span className="statistics_record_val">
                <span className="statistics_record_number">{ERROR.DASH}</span>
                <span className="statistics_record_unit">{t('ctn_mytrips_details_time_h_01')}</span>
                <span className="statistics_record_number">{ERROR.DASH}</span>
                <span className="statistics_record_unit">{t('ctn_mytrips_details_time_min_01')}</span>
              </span>
            </li>
            <div className="divider divider_vertical" />
            <li className="statistics_record_item">
              <span className="statistics_record_key">{t('ctn_mytrips_details_avg_speed')}</span>
              <span className="statistics_record_val">
                <span className="statistics_record_number">{ERROR.DASH}</span>
                <span className="statistics_record_unit">{defaultSpeedSymbol}</span>
              </span>
            </li>
            <div className="divider divider_vertical" />
            <li className="statistics_record_item">
              <span className="statistics_record_key">{t('ctn_mytrips_details_top_speed')}</span>
              <span className="statistics_record_val">
                <span className="statistics_record_number">{ERROR.DASH}</span>
                <span className="statistics_record_unit">{defaultSpeedSymbol}</span>
              </span>
            </li>
            <div className="divider divider_vertical" />
          </ul>
        </div>
      </main>
    </div>
  );
}

function FallbackError({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const today = new Date();
  const startDate = isSameYearAndMonth(selectedDate, today) ? today : startDateOfMonth(selectedDate);

  return (
    <div className="wrap has_driving_sheet">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title" />
      </Header>
      <main
        className="content page_my_trips"
        id="content"
      >
        <div className="driving_sheet_fixed">
          <div className="trip_calendar">
            <ReactDatePicker
              inline
              locale={getDateFnsLocale()}
              disabledKeyboardNavigation
              onChange={() => {}}
              selected={startDate}
              renderCustomHeader={({date}) => (
                <div className="react-datepicker__current-month">
                  <button
                    type="button"
                    aria-label="Previous Month"
                    className="react-datepicker__navigation react-datepicker__navigation--previous"
                  >
                    <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">{t('T10100')}</span>
                  </button>
                  {CldrDatetimeFormatter.format(date, 'yMMM')}
                  <button
                    type="button"
                    aria-label="Next Month"
                    className="react-datepicker__navigation react-datepicker__navigation--next"
                  >
                    <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">{t('T10101')}</span>
                  </button>
                </div>
              )}
            />
          </div>
          <div className="driving_sheet_header">
            <button className="btn_drawer" />
          </div>
        </div>
        <div className="driving_sheet_content">
          <div
            className="trip_summary"
            role="text"
            aria-label={`${CldrDatetimeFormatter.format(startDate, 'MMMMEEEEd')}${isSameDate(today, startDate) ? ` ${t('ctn_mytrips_details_today')}` : ''}`}
          >
            <div className="trip_date">
              <span className="trip_date_text">{CldrDatetimeFormatter.format(startDate, 'MMMMEEEEd')}</span>
              {isSameDate(today, startDate) && <span className="trip_date_today">{t('ctn_mytrips_details_today')}</span>}
            </div>
          </div>
          <div className="trip_info">
            <div className="empty_area">
              <span className="ic ic_caution_32px" />
              <span className="body_small_r">{t('msg_mytrips_recent_load_data')}</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
